import { ChangeEvent, useState } from "react";
import styled from "styled-components";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { addArticlesBulk } from "../RoomSettings/services/articles.service";
import { useTranslation } from "@coworker/locales";
import { useInputPopup } from "./index";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../RoomSettings/hooks/queryKeys";
import {
  extractProductArticleIdAndQuantity,
  isValidInput,
  MAX_QUANTITY_NUMBER,
  ParseStatus,
  removeDuplicateArticles,
  transformArticlesInfos,
} from "./AddMultipleArticles.helper";
import { ProductSearchComponent } from "@coworker/apprestructured/src/scan-and-search/pages/product-search-page/ProductSearchComponent";
import { fetchItemsInfoFromDataService } from "../../components/RoomSettings/services/localCombination.service";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { SearchItemCardRenderType } from "@coworker/apprestructured/src/scan-and-search/types/SearchItemCardRenderType";

const ModalItemWrapper = styled.div`
  padding: 0 24px;
`;
const Separator = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  padding-bottom: 16px;

  ::before,
  ::after {
    content: "";
    flex: 1;
    border-bottom: 2px solid var(--grey150);
  }

  :not(:empty)::before {
    margin-left: 24px;
    margin-right: 8px;
  }

  :not(:empty)::after {
    margin-left: 8px;
    margin-right: 24px;
  }
`;

interface AddMultipleArticlesDesktopType {
  isOpen: boolean;
  mediaId: string;
  groupId: string;
  countryId: string;
  setIsAddingArticles: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddMultipleArticlesDesktop = ({
  mediaId,
  groupId,
  setIsAddingArticles = () => {},
}: AddMultipleArticlesDesktopType) => {
  const { t } = useTranslation();
  const { closePopup } = useInputPopup();
  const storeId = useStoreId();
  const userCountryId = useCountryId();

  const [inputValue, setInputValue] = useState("");
  const parseStatus = isValidInput(inputValue);
  const uploading = false; // Placeholder for actual uploading state

  const queryClient = useQueryClient();

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = async () => {
    setIsAddingArticles(true);
    closePopup();
    const formattedInputValues = extractProductArticleIdAndQuantity(inputValue);
    const productArticleIds = formattedInputValues
      .map((o) => o.productArticleId)
      .join(",");
    const itemsInfo: Item[] = await queryClient.ensureQueryData({
      queryKey: ["items", "info", productArticleIds],
      queryFn: () => fetchItemsInfo(productArticleIds),
    });

    const allItemsInfo = [...itemsInfo];

    // Filter articles without article data and fetch their info from data-service
    const articlesWithoutData = formattedInputValues.filter(
      (article) =>
        !itemsInfo.some((item) => item.no === article.productArticleId)
    );

    if (articlesWithoutData.length > 0) {
      const missingNos = articlesWithoutData
        .map((a) => a.productArticleId)
        .join(",");

      const fetchedCombinations =
        (await fetchItemsInfoFromDataService(
          storeId,
          userCountryId,
          missingNos
        )) || [];

      // Collect all combination item numbers
      const combinationEntries = fetchedCombinations.map((combo) => ({
        parentArticle: combo.no,
        parentArticleQuantity: formattedInputValues.find(
          (item) => item.productArticleId === combo.no
        )?.quantity,
        itemNos: combo?.itemsInCombination?.map((i) => i.item_no),
      }));

      // Update formattedInputValues and collect itemNos
      const allCombinationItemNos = combinationEntries.flatMap(
        ({ parentArticle, parentArticleQuantity, itemNos }) => {
          itemNos?.forEach((no, index) => {
            const quantity =
              fetchedCombinations.find((combo) => combo.no === parentArticle)
                ?.itemsInCombination?.[index]?.quantity || 1; // Default to 1 if quantity is not found

            formattedInputValues.push({
              productArticleId: no,
              quantity: quantity * (parentArticleQuantity ?? 1),
              parentArticle,
              articlesCountInCombination: quantity,
            });
          });
          return itemNos;
        }
      );

      // Fetch detailed info for all items in combinations
      const articlesInCombinationInfo: Item[] =
        await queryClient.ensureQueryData({
          queryKey: ["items", "info", allCombinationItemNos],
          queryFn: () => fetchItemsInfo(allCombinationItemNos.join(",")),
        });

      // Finally merge parent articles and combination articles
      allItemsInfo.push(...articlesInCombinationInfo, ...fetchedCombinations);
    }

    const allArticles = transformArticlesInfos(
      mediaId,
      formattedInputValues,
      allItemsInfo
    );

    const uniqueArticles = removeDuplicateArticles(allArticles);
    if (allArticles.length > 0) {
      try {
        await addArticlesBulk(uniqueArticles, groupId);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.ArticlesByMedia],
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.ArticlesByGroup],
          }),
        ]);
      } catch {
        alert(t("searchErrorString"));
        closePopup();
      }
    }
    setIsAddingArticles(false);
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleProductClick = (product: Item) => {
    setInputValue((prev) =>
      prev ? `${prev}\n${product.no}` : `${product.no}`
    );
  };

  return (
    <>
      <FixaModal
        visible
        handleCloseBtn={closePopup}
        focusLockProps={{ locked: false }}
      >
        <FixaSheets
          labelledById="add-articles"
          header={
            <FixaModalHeader
              ariaCloseTxt={t("closeActionString")}
              title={t("addArticlesString")}
            />
          }
          footer={
            <FixaModalFooter>
              <FixaButton
                data-testid="addMultipleArticles:submit"
                type="primary"
                onClick={handleSubmit}
                disabled={
                  uploading ||
                  !inputValue ||
                  parseStatus !== ParseStatus.ParseOk
                }
              >
                {t("addArticlesString")}
              </FixaButton>
            </FixaModalFooter>
          }
        >
          <ProductSearchComponent
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            itemCardRenderType={SearchItemCardRenderType.button}
            handleItemCardClick={handleProductClick}
            shouldHideSPRItems={false}
            shouldHideLocalCombinationItems={false}
          />

          <Separator>{t("orString")}</Separator>

          <ModalItemWrapper>
            <FixaFormField
              shouldValidate={
                inputValue.length > 0 && parseStatus !== ParseStatus.ParseOk
              }
              validation={
                parseStatus === ParseStatus.ParseErrorArticleNumber
                  ? {
                      msg: t("addArticleNumberString"),
                      id: "article-number",
                    }
                  : {
                      msg: t("addQuantityNumberString", {
                        maxQuantity: MAX_QUANTITY_NUMBER,
                      }),
                      id: "quantity-number",
                    }
              }
            >
              <FixaTextArea
                label={t("addArticleAndQuantityInformationString")}
                value={inputValue}
                rows={25}
                onChange={handleInputChange}
                data-testid="addMultipleArticles:input"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              />
            </FixaFormField>
          </ModalItemWrapper>
        </FixaSheets>
      </FixaModal>
    </>
  );
};
